/**
 * StudentSearchCombobox - Search and select a student from the school.
 *
 * Typeahead sibling of UserSearchCombobox, but sourced from the school's
 * `students` collection (students live outside the users collection). Renders a
 * floating results overlay so it can drop into an existing modal without a
 * nested search panel.
 *
 * Props:
 *   onSelect(student)      - Called with the picked student. The object carries
 *                            firstName, surname AND lastName (surname aliased) so
 *                            consumers that read either field work unchanged.
 *   excludeStudentIds      - Array of student ids to hide from results
 *   placeholder            - Input placeholder text
 *   autoFocus              - Focus the input on mount
 *   disabled               - Disable the control
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useSchoolFirestore } from '@/hooks';
import { Avatar } from '@/components/ui';
import { MagnifyingGlassIcon, CheckIcon } from '@heroicons/react/24/outline';

const StudentSearchCombobox = ({
  onSelect,
  excludeStudentIds = [],
  placeholder = 'Search for a student by name...',
  autoFocus = false,
  disabled = false,
}) => {
  const { getAll } = useSchoolFirestore();
  const [query, setQuery] = useState('');
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Load active students + classes once.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [studentDocs, classDocs] = await Promise.all([
          getAll('students'),
          getAll('classes'),
        ]);
        if (cancelled) return;
        setStudents(
          studentDocs.filter(s => s.status !== 'inactive' && s.status !== 'transferred')
        );
        setClasses(classDocs);
      } catch (err) {
        console.error('[StudentSearchCombobox] Load error:', err);
        if (!cancelled) setStudents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [getAll]);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  const classMap = useMemo(() => {
    const map = {};
    classes.forEach(c => { map[c.id] = c.name || c.className || ''; });
    return map;
  }, [classes]);

  const excludeSet = useMemo(
    () => new Set((excludeStudentIds || []).filter(Boolean)),
    [excludeStudentIds]
  );

  // Filter on query (all terms must match name).
  const filtered = useMemo(() => {
    if (!query.trim()) return [];
    const terms = query.toLowerCase().split(' ').filter(Boolean);
    const results = students.filter(s => {
      if (excludeSet.has(s.id)) return false;
      const name = `${s.firstName || ''} ${s.surname || ''}`.toLowerCase();
      return terms.every(t => name.includes(t));
    });
    results.sort((a, b) =>
      `${a.surname || ''} ${a.firstName || ''}`.localeCompare(`${b.surname || ''} ${b.firstName || ''}`)
    );
    return results.slice(0, 10);
  }, [query, students, excludeSet]);

  useEffect(() => {
    setIsOpen(filtered.length > 0 && query.trim().length > 0);
    setSelectedIndex(0);
  }, [filtered, query]);

  const handleSelect = (student) => {
    // Alias surname -> lastName so consumers reading either field work.
    onSelect({
      ...student,
      lastName: student.lastName || student.surname || '',
      className: student.className || classMap[student.classId] || '',
    });
    setQuery('');
    setIsOpen(false);
    setSelectedIndex(0);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || filtered.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < filtered.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filtered[selectedIndex]) handleSelect(filtered[selectedIndex]);
        break;
      case 'Escape':
        setIsOpen(false);
        setQuery('');
        break;
    }
  };

  // Close on outside click.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        inputRef.current && !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query && setIsOpen(filtered.length > 0)}
          placeholder={placeholder}
          disabled={disabled || loading}
          className="input input-bordered input-sm w-full pl-9"
        />
        <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
        {loading && (
          <span className="loading loading-spinner loading-xs absolute right-3 top-1/2 -translate-y-1/2" />
        )}
      </div>

      {isOpen && filtered.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-base-100 rounded-lg shadow-xl border border-base-300 overflow-hidden"
        >
          <div className="max-h-64 overflow-y-auto">
            {filtered.map((student, index) => (
              <div
                key={student.id}
                onClick={() => handleSelect(student)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                  index === selectedIndex ? 'bg-primary/10' : 'hover:bg-base-200'
                }`}
              >
                <Avatar
                  src={student.photoURL}
                  name={`${student.firstName || ''} ${student.surname || ''}`.trim()}
                  size="sm"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {student.firstName} {student.surname}
                  </div>
                  {student.classId && classMap[student.classId] && (
                    <div className="text-xs text-base-content/60 truncate">
                      {classMap[student.classId]}
                    </div>
                  )}
                </div>
                {index === selectedIndex && (
                  <CheckIcon className="w-4 h-4 text-primary flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentSearchCombobox;
